import {
  type GamePlatform,
  type PublishingDestination,
  PublishingDestinationSchema,
  type PublishingStore,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

type RawDestinationRow = {
  id: unknown;
  game_id: unknown;
  platform: unknown;
  store: unknown;
  store_url: unknown;
  created_at: unknown;
  updated_at: unknown;
};

function mapDestinationRow(raw: RawDestinationRow): Record<string, unknown> {
  return {
    id: raw.id,
    gameId: raw.game_id,
    platform: raw.platform,
    store: raw.store,
    storeUrl: raw.store_url,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export async function fetchPublishingDestinations(
  gameId: string,
): Promise<PublishingDestination[]> {
  const { data, error } = await supabase
    .from('game_publishing_destinations')
    .select('*')
    .eq('game_id', gameId)
    .order('created_at', { ascending: true });

  if (error) {
    console.warn('[publishing-destinations] Failed to load:', error.message);
    return [];
  }

  const destinations: PublishingDestination[] = [];
  for (const raw of (data ?? []) as RawDestinationRow[]) {
    const parsed = PublishingDestinationSchema.safeParse(mapDestinationRow(raw));
    if (parsed.success) {
      destinations.push(parsed.data);
    } else {
      console.warn(
        '[publishing-destinations] Could not parse row; skipping.',
        parsed.error.issues,
      );
    }
  }
  return destinations;
}

export async function addPublishingDestination(input: {
  gameId: string;
  platform: GamePlatform;
  store: PublishingStore;
  storeUrl?: string | null;
}): Promise<PublishingDestination | null> {
  const { data, error } = await supabase
    .from('game_publishing_destinations')
    .insert({
      game_id: input.gameId,
      platform: input.platform,
      store: input.store,
      store_url: input.storeUrl ?? null,
    })
    .select('*')
    .single();

  if (error) {
    console.warn('[publishing-destinations] Failed to add:', error.message);
    return null;
  }

  const parsed = PublishingDestinationSchema.safeParse(
    mapDestinationRow(data as RawDestinationRow),
  );
  if (!parsed.success) {
    console.warn(
      '[publishing-destinations] Could not parse inserted row.',
      parsed.error.issues,
    );
    return null;
  }
  return parsed.data;
}

export async function updatePublishingDestinationUrl(
  id: string,
  storeUrl: string | null,
): Promise<PublishingDestination | null> {
  const { data, error } = await supabase
    .from('game_publishing_destinations')
    .update({ store_url: storeUrl })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.warn('[publishing-destinations] Failed to update URL:', error.message);
    return null;
  }

  const parsed = PublishingDestinationSchema.safeParse(
    mapDestinationRow(data as RawDestinationRow),
  );
  if (!parsed.success) {
    console.warn(
      '[publishing-destinations] Could not parse updated row.',
      parsed.error.issues,
    );
    return null;
  }
  return parsed.data;
}

export async function removePublishingDestination(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('game_publishing_destinations')
    .delete()
    .eq('id', id);

  if (error) {
    console.warn('[publishing-destinations] Failed to delete:', error.message);
    return false;
  }
  return true;
}
